import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Upload, Share2, Paperclip, Download } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  { v: "ordonnance", label: "Ordonnance" },
  { v: "analyse", label: "Analyses" },
  { v: "imagerie", label: "Imagerie" },
  { v: "compte-rendu", label: "Compte-rendu" },
  { v: "vaccin", label: "Vaccination" },
  { v: "autre", label: "Autre" },
];

const CAT_COLOR = {
  ordonnance: "bg-emerald-50 text-emerald-800 border-emerald-200",
  analyse: "bg-sky-50 text-sky-800 border-sky-200",
  imagerie: "bg-violet-50 text-violet-800 border-violet-200",
  "compte-rendu": "bg-amber-50 text-amber-800 border-amber-200",
  vaccin: "bg-pink-50 text-pink-800 border-pink-200",
  autre: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function DMP({ patientIdProp }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "autre", content: "" });
  const [fileForm, setFileForm] = useState({ title: "", description: "", category: "autre", file: null });
  const fileInputRef = useRef(null);

  const isDoctorContext = user?.role === "doctor" && patientIdProp;

  const load = async () => {
    try {
      const params = isDoctorContext ? { patient_id: patientIdProp } : {};
      const { data } = await api.get("/dmp", { params });
      setDocs(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientIdProp]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (isDoctorContext) payload.patient_id = patientIdProp;
      await api.post("/dmp", payload);
      setOpen(false);
      setForm({ title: "", description: "", category: "autre", content: "" });
      toast.success("Document ajouté au DMP");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const submitFile = async (e) => {
    e.preventDefault();
    if (!fileForm.file) return toast.error("Sélectionnez un fichier");
    const fd = new FormData();
    fd.append("file", fileForm.file);
    fd.append("title", fileForm.title || fileForm.file.name);
    fd.append("description", fileForm.description);
    fd.append("category", fileForm.category);
    if (isDoctorContext) fd.append("patient_id", patientIdProp);
    try {
      await api.post("/dmp/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileOpen(false);
      setFileForm({ title: "", description: "", category: "autre", file: null });
      toast.success("Fichier importé dans le DMP");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#FF7A5C] mb-2">Dossier Médical Partagé</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Mon DMP</h1>
          <p className="text-slate-500 mt-2">Ordonnances, analyses, comptes-rendus — tout au même endroit.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={fileOpen} onOpenChange={setFileOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#0FACD7] text-[#0FACD7] hover:bg-cyan-50 font-semibold" data-testid="dmp-file-upload-btn"><Paperclip className="h-4 w-4 mr-2" /> Importer un fichier</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Importer un fichier (PDF, image…)</DialogTitle></DialogHeader>
              <form onSubmit={submitFile} className="space-y-4 mt-2">
                <div>
                  <Label>Fichier (max 8 Mo)</Label>
                  <Input ref={fileInputRef} required type="file" accept="application/pdf,image/*,.jpg,.jpeg,.png,.webp" onChange={(e) => setFileForm({ ...fileForm, file: e.target.files?.[0] || null })} className="mt-1.5" data-testid="dmp-file-input" />
                </div>
                <div>
                  <Label>Titre</Label>
                  <Input value={fileForm.title} onChange={(e) => setFileForm({ ...fileForm, title: e.target.value })} placeholder={fileForm.file?.name || ""} className="mt-1.5" data-testid="dmp-file-title-input" />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={fileForm.category} onValueChange={(v) => setFileForm({ ...fileForm, category: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={fileForm.description} onChange={(e) => setFileForm({ ...fileForm, description: e.target.value })} className="mt-1.5" />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="dmp-file-submit-btn">Importer</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-coral font-semibold" data-testid="dmp-upload-btn"><Upload className="h-4 w-4 mr-2" /> Ajouter une note</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Nouvelle note / document DMP</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4 mt-2">
                <div>
                  <Label>Titre</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="dmp-title-input" />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="dmp-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Contenu / Notes</Label>
                  <Textarea required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} className="mt-1.5" data-testid="dmp-content-input" />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="dmp-submit-btn">Enregistrer</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500">
          <FileText className="mx-auto h-10 w-10 text-slate-300 mb-3" />
          Votre DMP est vide. Ajoutez votre premier document.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {docs.map((d) => (
            <div key={d.document_id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`dmp-doc-${d.document_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${CAT_COLOR[d.category] || CAT_COLOR.autre}`}>{d.category}</div>
                  <div className="font-display font-semibold mt-2">{d.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{new Date(d.created_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}</div>
                </div>
                <Share2 className="h-4 w-4 text-slate-400" />
              </div>
              {d.description && <p className="text-sm text-slate-600 mt-3">{d.description}</p>}
              {d.file_id ? (
                <a href={`${API_URL}/dmp/file/${d.file_id}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#0FACD7] hover:text-[#FF7A5C] font-semibold" data-testid={`download-${d.document_id}`}>
                  <Download className="h-3.5 w-3.5" /> Ouvrir {d.filename || "le fichier"}
                </a>
              ) : (
                <pre className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded p-3 max-h-32 overflow-auto whitespace-pre-wrap font-mono">{d.content}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
